/**
 * training-group-members.js
 */

import React from 'react';
import axios from 'axios';
import { Checkbox } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert } from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { UserContext } from 'contexts/user/user-context';
import { Types } from 'utils/constants';
import { isValidEmail } from 'utils/user-profile-validation';
import FeatureBox from 'components/feature-box';
import MemberList from 'components/groups/member-list';
import MemberSelector from 'components/groups/member-selector';
import AcceptButton from 'components/groups/accept-button';
import sortMembers from 'components/groups/sort-members';
import { ConnectedHandlersContext } from '../connections/connected-handlers-context';
import RequestType from '../request-type';
import MemberRequestDialog from './member-request-dialog';


const useStyles = makeStyles(theme => ({
  error: {
    marginTop: theme.spacing(1)
  },
  approveButton: {
    padding: 6
  },
  memberSelector: {
    marginTop: theme.spacing(3)
  }
}));


const filterOptions = createFilterOptions({
  stringify: option => `${option.name} ${option.email}`
});


export default function TrainingGroupMembers(props) {
  const { group, showEditor, canEdit } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { allHandlers, managedHandlerIds } = React.useContext(ConnectedHandlersContext);
  const formContext = useFormContext();
  const { control, setValue, formState: { errors } } = formContext;
  const members = useWatch({ control, name: 'members' });
  const memberRequests = useWatch({ control, name: 'memberRequests' });
  const [selectorFilterText, setSelectorFilterText] = React.useState('');
  const [isRequestDialogOpen, setIsRequestDialogOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const memberEmails = React.useMemo(() => members?.map(m => m.email.toLowerCase()) || [], [members]);
  const pendingEmails = React.useMemo(() => memberRequests?.map(i => i.email.toLowerCase()) || [], [memberRequests]);
  const disabledReason = !group.membership?.dateAccepted
    ? 'You must be a member and a leader to make group changes'
    : 'You must be a leader to make group changes';

  
  const onValidateMembers = (members) => {
    if (members.length === 0) {
      return 'You must have at least one member';
    } else if (!members.find(m => m.isLeader)) {
      return 'You must have at least one leader';
    } else if (user.role.isHandler && group.id === 0 && !members.find(m => m.id === user.id)) {
      return 'You must be a member when creating a new group';
    } else if (user.role.isTrainer && group.id === 0 && !members.find(m => managedHandlerIds.includes(m.id))) {
      return 'At least one of your trained handlers must be a member when creating a new group';
    } else {
      return true;
    }
  }


  const filterSearchInOptions = (options, params) => {
    const filteredOptions = filterOptions(options, params);
    const isEmail = isValidEmail(params.inputValue);

    if (isEmail && memberEmails.includes(params.inputValue.toLowerCase())) {
      return [{
        id: 'error',
        name: 'This user is already a group member',
        disabled: true
      }];
    } else if (isEmail && pendingEmails.includes(params.inputValue.toLowerCase())) {
      return [{
        id: 'error',
        name: 'This user has already been requested',
        disabled: true
      }];
    } else {
      return [
        {
          id: 'request',
          action: isEmail && filteredOptions.length === 0 ? 'email' : 'dialog',
          name: isEmail && filteredOptions.length === 0 
            ? `INVITE ${params.inputValue}`
            : `INVITE A NEW MEMBER ...`,
          validEmail: isEmail ? params.inputValue : '',
          doClose: true,
        },
        ...filteredOptions
      ];
    }
  }


  const onAdd = async (member) => {
    if (typeof(member) === 'string') {
      return false;
    } else if (member.action === 'email') {
      return await onRequestMembership(member.validEmail);
    } else if (member.action === 'dialog') {
      setIsRequestDialogOpen(true);
      return true;
    } else {
      onAddMember(member);
      return true;
    }
  }


  const onAddMember = (member) => {
    const newMembers = [...members, { ...member, isLeader: true }].sort(sortMembers(user));
    setValue('members', newMembers, { shouldDirty: true, shouldValidate: Object.keys(errors).length > 0 });

    // check if this user is currently in the pending list and remove if so
    if (pendingEmails.includes(member.email.toLowerCase())) {
      const newPending = memberRequests.filter(m => m.id !== member.id).sort(sortMembers(user));
      setValue('memberRequests', newPending, { shouldDirty: true, shouldValidate: Object.keys(errors).length > 0 });
    }
  }


  const onRequestMembership = async (email) => {
    setIsLoading(true);
    const response = await axios.get(`/groups/${group.id}/member-request?email=${encodeURIComponent(email)}`);
    setIsLoading(false);
    if (response.status === 200) {
      onAddPending(response.data);
      return true;
    } else {
      return false;
    }
  }


  const onAddPending = async (member) => {
    const newPending = [...(memberRequests || []), member];
    setValue('memberRequests', newPending, { shouldDirty: true });
  }


  const onApprovePending = (member) => {
    onAddMember(member);
    const newPending = memberRequests.filter(m => m.id !== member.id);
    setValue('memberRequests', newPending, { shouldDirty: true, shouldValidate: Object.keys(errors).length > 0 });
  }


  return (
    <>
      <FeatureBox loading={isLoading}>

        {/* MEMBERS */}
        <Controller
          control={control}
          name='members'
          defaultValue={null}
          rules={{ validate: onValidateMembers }}
          render={({ field }) => (
            <MemberList
              headerLabels={['Members', 'Leader']}
              members={field.value}
              onChange={field.onChange}
              canRemove={(member) => canEdit}
              removeResultReason={(member) => canEdit ? '' : disabledReason}
              renderers={[
                (member) => (
                  <Checkbox
                    color='primary'
                    checked={member.isLeader}
                    disabled={!canEdit}
                    onChange={(e, v) => {
                      const newMembers = field.value.map(m => m.id === member.id
                        ? { ...m, isLeader: v }
                        : m);
                      field.onChange(newMembers);
                    }}
                  />
                )
              ]}
            />
          )}
        />
        {errors.members &&
        <Alert severity='error' className={classes.error}>{errors.members.message}</Alert>}

        {/* MEMBER REQUESTS */}
        {showEditor &&
        <Controller
          control={control}
          name='memberRequests'
          defaultValue={null}
          render={({ field }) => <>
            {field.value && field.value.length > 0 && 
            <MemberList
              headerLabels={['Member Requests']}
              members={field.value}
              onChange={field.onChange}
              canRemove={(member) => canEdit}
              removeResultReason={(member) => canEdit ? '' : disabledReason}
              userColExtra={(member) => <RequestType context='training-member' request={member} />}
              renderers={[
                (member) => member.type === Types.MemberRequest.FROM_USER
                  ? <AcceptButton onClick={() => onApprovePending(member)} disabled={!canEdit} />
                  : null
              ]}
            />}
          </>}
        />}

        {/* MEMBER SELECTOR */}
        {showEditor &&
        <div className={classes.memberSelector}>
          <MemberSelector
            options={allHandlers}
            members={members}
            onInputChange={(value) => setSelectorFilterText(value)}
            filterOptions={filterSearchInOptions}
            onAdd={onAdd}
            disabledReason={canEdit ? '' : disabledReason}
          />
        </div>}

      </FeatureBox>

      {/* MEMBER REQUEST DIALOG */}
      <MemberRequestDialog
        isOpen={isRequestDialogOpen}
        onClose={() => setIsRequestDialogOpen(false)}
        onAddPending={onAddPending}
        onAddMember={onAddMember}      
        inputText={selectorFilterText}
        group={group}
        options={allHandlers}
        memberEmails={memberEmails}
        pendingEmails={pendingEmails}
      />
    </>
  );
}